import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "../../components/ui/use-toast";
import { methodologyService } from "../../lib/firebase";
import { useSystemSettings } from "../../lib/SystemSettingsContext";

const ADMIN_EMAIL = "admin@rawpa.com";

export const AdminSettings = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const auth = getAuth();
  const { settings, refreshSettings } = useSystemSettings();

  // Settings state
  const [disableLogin, setDisableLogin] = useState(settings.disableLogin || false);
  const [disableSignup, setDisableSignup] = useState(settings.disableSignup || false);
  const [disableAI, setDisableAI] = useState(settings.disableAI || false);
  const [disableStraightUpMethodologies, setDisableStraightUpMethodologies] = useState(settings.disableStraightUpMethodologies || false);
  const [disableGuidedMethodologies, setDisableGuidedMethodologies] = useState(settings.disableGuidedMethodologies || false);
  const [disablePathway, setDisablePathway] = useState(settings.disablePathway || false);
  const [disablePentestOrchestrator, setDisablePentestOrchestrator] = useState(settings.disablePentestOrchestrator || false);
  const [disableHuntersBoard, setDisableHuntersBoard] = useState(settings.disableHuntersBoard || false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Featured methodology and toolkit state
  const [featuredMethodology, setFeaturedMethodology] = useState(settings.featuredMethodology);
  const [featuredToolkit, setFeaturedToolkit] = useState(settings.featuredToolkit);
  
  // Methodology options for featuring
  const [methodologyOptions, setMethodologyOptions] = useState<Array<{
    id: string;
    title: string;
    type: 'straight-up' | 'guided' | 'pathway';
    description?: string;
  }>>([]);
  
  // Toolkit options for featuring
  const [toolkitOptions, setToolkitOptions] = useState<Array<{
    id: string;
    title: string;
    type: 'lolbas' | 'gtfobins' | 'wadcoms' | 'reverse-shell' | 'osint' | 'hunting-toolkit';
    description?: string;
  }>>([
    { id: 'lolbas', title: 'LOLBAS', type: 'lolbas', description: 'Living Off The Land Binaries And Scripts' },
    { id: 'gtfobins', title: 'GTFOBins', type: 'gtfobins', description: 'Unix binaries that can be exploited' },
    { id: 'wadcoms', title: 'WADComs', type: 'wadcoms', description: 'Windows Attack and Defense Commands' },
    { id: 'reverse-shell', title: 'Reverse Shell', type: 'reverse-shell', description: 'Reverse shell payloads and techniques' },
    { id: 'osint', title: 'OSINT Toolkit', type: 'osint', description: 'Open Source Intelligence tools' },
    { id: 'hunting-toolkit', title: 'Hunting Toolkit', type: 'hunting-toolkit', description: 'Threat hunting commands and techniques' }
  ]);

  // Load all methodologies for the select dropdown
  useEffect(() => {
    const loadMethodologyOptions = async () => {
      try {
        // Load straight-up methodologies
        const straightUpMethodologies = await methodologyService.getMethodologies();
        const straightUpOptions = straightUpMethodologies.map(m => ({
          id: m.id,
          title: m.title,
          type: m.id === 'pathway' ? 'pathway' as const : 'straight-up' as const,
          description: m.description
        }));
        
        // Load guided methodologies
        const guidedMethodologies = await methodologyService.getGuidedMethodologies();
        const guidedOptions = guidedMethodologies
          .filter(m => m.status === 'Active')
          .map(m => ({
            id: m.id,
            title: `Guided: ${m.name}`,
            type: 'guided' as const,
            description: m.description
          }));
        
        // Combine all options
        setMethodologyOptions([...straightUpOptions, ...guidedOptions]);
      } catch (error) {
        console.error("Error loading methodologies:", error);
        toast({
          title: "Error",
          description: "Failed to load methodologies. Please refresh the page.",
          variant: "destructive",
        });
      }
    };
    
    loadMethodologyOptions();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, auth]);

  // Update local state when settings change
  useEffect(() => {
    setDisableLogin(settings.disableLogin || false);
    setDisableSignup(settings.disableSignup || false);
    setDisableAI(settings.disableAI || false);
    setDisableStraightUpMethodologies(settings.disableStraightUpMethodologies || false);
    setDisableGuidedMethodologies(settings.disableGuidedMethodologies || false);
    setDisablePathway(settings.disablePathway || false);
    setDisablePentestOrchestrator(settings.disablePentestOrchestrator || false);
    setDisableHuntersBoard(settings.disableHuntersBoard || false);
    if (settings.featuredMethodology !== undefined) {
      setFeaturedMethodology(settings.featuredMethodology);
    }
    if (settings.featuredToolkit !== undefined) {
      setFeaturedToolkit(settings.featuredToolkit);
    }
  }, [settings]);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/aAyuiOcvMq');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await methodologyService.updateSystemSettings({
        disableLogin,
        disableSignup,
        disableAI,
        disableStraightUpMethodologies,
        disableGuidedMethodologies,
        disablePathway,
        disablePentestOrchestrator,
        disableHuntersBoard
      });
      
      // Refresh the settings in the context
      await refreshSettings();
      
      toast({
        title: "Settings saved",
        description: "Your system settings have been updated successfully.",
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle featuring a methodology
  const handleFeatureMethodology = async (methodologyId: string) => {
    setIsSaving(true);
    try {
      if (methodologyId === "none") {
        // Clear featured methodology
        const updatedSettings = {
          ...settings,
          featuredMethodology: undefined
        };
        await methodologyService.updateSystemSettings(updatedSettings);
        setFeaturedMethodology(undefined);
      } else {
        const selected = methodologyOptions.find(m => m.id === methodologyId);
        if (selected) {
          const newFeaturedMethodology = {
            id: selected.id,
            title: selected.title,
            type: selected.type,
            description: selected.description
          };
          const updatedSettings = {
            ...settings,
            featuredMethodology: newFeaturedMethodology
          };
          await methodologyService.updateSystemSettings(updatedSettings);
          setFeaturedMethodology(newFeaturedMethodology);
        }
      }
      
      // Refresh the settings in the context
      await refreshSettings();
      
      toast({
        title: "Featured methodology updated",
        description: "Your changes have been saved successfully.",
      });
    } catch (error) {
      console.error("Error updating featured methodology:", error);
      toast({
        title: "Error",
        description: "Failed to update featured methodology. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle featuring a toolkit
  const handleFeatureToolkit = async (toolkitId: string) => {
    setIsSaving(true);
    try {
      if (toolkitId === "none") {
        // Clear featured toolkit
        const updatedSettings = {
          ...settings,
          featuredToolkit: undefined
        };
        await methodologyService.updateSystemSettings(updatedSettings);
        setFeaturedToolkit(undefined);
      } else {
        const selected = toolkitOptions.find(t => t.id === toolkitId);
        if (selected) {
          const newFeaturedToolkit = {
            id: selected.id,
            title: selected.title,
            type: selected.type,
            description: selected.description
          };
          const updatedSettings = {
            ...settings,
            featuredToolkit: newFeaturedToolkit
          };
          await methodologyService.updateSystemSettings(updatedSettings);
          setFeaturedToolkit(newFeaturedToolkit);
        }
      }
      
      // Refresh the settings in the context
      await refreshSettings();
      
      toast({
        title: "Featured toolkit updated",
        description: "Your changes have been saved successfully.",
      });
    } catch (error) {
      console.error("Error updating featured toolkit:", error);
      toast({
        title: "Error",
        description: "Failed to update featured toolkit. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA Admin</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/aAyuiOcvMq/admin')}
                className="font-['Space_Grotesk'] text-black"
              >
                Back to Dashboard
              </Button>
              <div 
                onClick={handleSignOut}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-8">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              System Settings
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
              Configure global system settings and feature availability
            </p>
          </div>

          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Authentication Settings</CardTitle>
                <CardDescription>Control user access to the platform</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-login" className="font-medium">Disable Login</Label>
                    <p className="text-sm text-gray-500">Prevents users from logging into the platform</p>
                  </div>
                  <Switch 
                    id="disable-login" 
                    checked={disableLogin}
                    onCheckedChange={(checked) => setDisableLogin(checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-signup" className="font-medium">Disable Signup</Label>
                    <p className="text-sm text-gray-500">Prevents new users from creating accounts</p>
                  </div>
                  <Switch 
                    id="disable-signup" 
                    checked={disableSignup}
                    onCheckedChange={(checked) => setDisableSignup(checked)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Feature Settings</CardTitle>
                <CardDescription>Control which features are available to users</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-ai" className="font-medium">Disable AI Features</Label>
                    <p className="text-sm text-gray-500">Turns off all AI-related functionality</p>
                  </div>
                  <Switch 
                    id="disable-ai" 
                    checked={disableAI}
                    onCheckedChange={(checked) => setDisableAI(checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-straight-up" className="font-medium">Disable Straight-up Methodologies</Label>
                    <p className="text-sm text-gray-500">Hides all straight-up methodology content</p>
                  </div>
                  <Switch 
                    id="disable-straight-up" 
                    checked={disableStraightUpMethodologies}
                    onCheckedChange={(checked) => setDisableStraightUpMethodologies(checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-guided" className="font-medium">Disable Guided Methodologies</Label>
                    <p className="text-sm text-gray-500">Hides all guided methodology content</p>
                  </div>
                  <Switch 
                    id="disable-guided" 
                    checked={disableGuidedMethodologies}
                    onCheckedChange={(checked) => setDisableGuidedMethodologies(checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-pathway" className="font-medium">Disable Pathway Methodology</Label>
                    <p className="text-sm text-gray-500">Hides all pathway methodology content</p>
                  </div>
                  <Switch 
                    id="disable-pathway" 
                    checked={disablePathway}
                    onCheckedChange={(checked) => setDisablePathway(checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-pentest-orchestrator" className="font-medium">Disable Pentest Orchestrator</Label>
                    <p className="text-sm text-gray-500">Turns off the Pentest Orchestrator for all users. When disabled, users see 'Under development, check again later.'</p>
                  </div>
                  <Switch
                    id="disable-pentest-orchestrator"
                    checked={disablePentestOrchestrator}
                    onCheckedChange={setDisablePentestOrchestrator}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-hunters-board" className="font-medium">Disable Hunter's Board</Label>
                    <p className="text-sm text-gray-500">Turns off the Hunter's Board for all users. When disabled, users see 'Under development, check again later.'</p>
                  </div>
                  <Switch
                    id="disable-hunters-board"
                    checked={disableHuntersBoard}
                    onCheckedChange={setDisableHuntersBoard}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Featured Methodology Section */}
            <Card>
              <CardHeader>
                <CardTitle>Featured Methodology</CardTitle>
                <CardDescription>Select a methodology to feature on the user dashboard</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="featured-methodology" className="font-medium">Select Methodology to Feature</Label>
                  <p className="text-sm text-gray-500 mb-2">
                    This methodology will be highlighted on the user dashboard
                  </p>
                  
                  <Select 
                    value={featuredMethodology?.id || "none"} 
                    onValueChange={handleFeatureMethodology}
                    disabled={isSaving}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a methodology to feature" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="none" value="none">None</SelectItem>
                      {methodologyOptions.map(option => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {featuredMethodology && (
                  <div className="p-4 border rounded-lg bg-[#f9f9f9]">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium">Currently Featured:</h3>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleFeatureMethodology("none")}
                        disabled={isSaving}
                        className="text-xs h-8"
                      >
                        {isSaving ? "Saving..." : "Remove"}
                      </Button>
                    </div>
                    <p className="font-medium">{featuredMethodology.title}</p>
                    {featuredMethodology.description && (
                      <p className="text-sm text-gray-600 mt-1">{featuredMethodology.description}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Type: {featuredMethodology.type === 'straight-up' 
                        ? 'Straight-up Methodology' 
                        : featuredMethodology.type === 'guided'
                          ? 'Guided Methodology'
                          : 'Pathway Methodology'
                      }
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Featured Toolkit Section */}
            <Card>
              <CardHeader>
                <CardTitle>Featured Toolkit</CardTitle>
                <CardDescription>Select a toolkit to feature on the user dashboard</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="featured-toolkit" className="font-medium">Select Toolkit to Feature</Label>
                  <p className="text-sm text-gray-500 mb-2">
                    This toolkit will be highlighted on the user dashboard
                  </p>
                  
                  <Select 
                    value={featuredToolkit?.id || "none"} 
                    onValueChange={handleFeatureToolkit}
                    disabled={isSaving}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a toolkit to feature" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="none" value="none">None</SelectItem>
                      {toolkitOptions.map(option => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {featuredToolkit && (
                  <div className="p-4 border rounded-lg bg-[#f9f9f9]">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium">Currently Featured:</h3>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleFeatureToolkit("none")}
                        disabled={isSaving}
                        className="text-xs h-8"
                      >
                        {isSaving ? "Saving..." : "Remove"}
                      </Button>
                    </div>
                    <p className="font-medium">{featuredToolkit.title}</p>
                    {featuredToolkit.description && (
                      <p className="text-sm text-gray-600 mt-1">{featuredToolkit.description}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Type: {featuredToolkit.type === 'lolbas' 
                        ? 'LOLBAS' 
                        : featuredToolkit.type === 'gtfobins'
                          ? 'GTFOBins'
                          : featuredToolkit.type === 'wadcoms'
                            ? 'WADComs'
                            : featuredToolkit.type === 'reverse-shell'
                              ? 'Reverse Shell'
                              : featuredToolkit.type === 'osint'
                                ? 'OSINT Toolkit'
                                : 'Hunting Toolkit'
                      }
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end mt-4">
              <Button 
                className="bg-[#daed6e] hover:bg-[#c5d85c] text-black font-medium px-6"
                onClick={(e) => {
                  e.preventDefault();
                  handleSaveSettings();
                }}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Settings"}
              </Button>
            </div>

            <Card className="border-dashed border-gray-300 bg-gray-50">
              <CardHeader>
                <CardTitle>Coming Soon</CardTitle>
                <CardDescription>More configuration options will be available in future updates</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="list-disc pl-5 space-y-2 text-gray-500">
                  <li>User role management</li>
                  <li>Content moderation settings</li>
                  <li>Custom branding options</li>
                  <li>API access controls</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}; 